<script setup lang="ts">
import { FocusTrap } from '@headlessui/vue'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import type { DateInputProps, DateInputRange, DateInputValue } from './types'

import { useClickOutside } from '@/shared/composables/useClickOutside'
import { dt, useFormat } from '@/shared/lib/format'
import { useId } from '@/shared/ui/composables'
import CalendarView from '@/shared/ui/date-picker/CalendarView.vue'
import { startOfDay } from '@/shared/ui/date-picker/logic'
import { useFloating } from '@/shared/ui/floating'
import { Icon } from '@/shared/ui/icon'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'

const props = withDefaults(defineProps<DateInputProps>(), {
  selectionMode: 'single',
  label: '',
  placeholder: '',
  errorMessage: '',
  size: 'md',
  disabled: false,
  invalid: false,
  placement: 'bottom-start',
})

const model = defineModel<DateInputValue>({ default: null })
const emit = defineEmits<{
  change: [value: DateInputValue]
  open: []
  close: []
}>()

const fmt = useFormat()
const focusFeatures =
  FocusTrap.features.InitialFocus | FocusTrap.features.TabLock | FocusTrap.features.RestoreFocus
const triggerId = useId('date-input')
const panelId = useId('date-input-panel')
const errorId = useId('date-input-error')
const trigger = ref<HTMLElement | null>(null)
const floating = ref<HTMLElement | null>(null)
const open = ref(false)
const draftStart = ref<Date | null>(null)
const draftEnd = ref<Date | null>(null)
const triggerSizeClasses: Record<NonNullable<DateInputProps['size']>, string> = {
  xs: 'h-7 text-xs',
  sm: 'h-8 text-xs',
  md: 'h-10 text-xs',
  lg: 'h-12 text-sm',
  xl: 'h-14 text-sm',
}

const { floatingStyles } = useFloating(trigger, floating, {
  placement: props.placement,
  offset: 8,
  strategy: 'fixed',
})

const minDay = computed(() => startOfDay(props.min))
const maxDay = computed(() => startOfDay(props.max ?? new Date())!)

function validDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

function rangeValue(value: DateInputValue): DateInputRange | null {
  if (!value || validDate(value)) return null
  return value
}

function formatDate(date: Date): string {
  return props.format ? props.format(date) : dt.formatDate(date, fmt.locale)
}

const displayValue = computed(() => {
  if (props.selectionMode === 'single') return validDate(model.value) ? formatDate(model.value) : ''
  const range = rangeValue(model.value)
  if (!range || !validDate(range.start)) return ''
  const start = formatDate(range.start)
  return validDate(range.end) ? `${start}${props.rangeSeparator}${formatDate(range.end)}` : start
})

function hydrateDraft(): void {
  if (props.selectionMode === 'single') {
    draftStart.value = validDate(model.value) ? startOfDay(model.value) : null
    draftEnd.value = draftStart.value
    return
  }
  const range = rangeValue(model.value)
  draftStart.value = validDate(range?.start) ? startOfDay(range.start) : null
  draftEnd.value = validDate(range?.end) ? startOfDay(range.end) : null
}

async function focusCalendar(): Promise<void> {
  await nextTick()
  await nextTick()
  const selected = floating.value?.querySelector<HTMLButtonElement>(
    '.ui-date-picker__day.is-primary-edge:not(:disabled)',
  )
  const enabled = floating.value?.querySelectorAll<HTMLButtonElement>(
    '.ui-date-picker__day:not(:disabled)',
  )
  const focusTarget = selected ?? enabled?.item(Math.max(0, (enabled?.length ?? 1) - 1))
  focusTarget?.focus()
}

function showCalendar(): void {
  if (props.disabled || open.value) return
  hydrateDraft()
  open.value = true
  emit('open')
  void focusCalendar()
}

function hideCalendar(restoreFocus = false): void {
  if (!open.value) return
  open.value = false
  hydrateDraft()
  emit('close')
  if (restoreFocus) void nextTick(() => trigger.value?.focus())
}

function toggleCalendar(): void {
  if (open.value) hideCalendar()
  else showCalendar()
}

function commit(value: DateInputValue): void {
  model.value = value
  emit('change', value)
  hideCalendar(true)
}

function selectDay(date: Date): void {
  if (props.disabled) return
  const clicked = startOfDay(date)!
  if ((minDay.value && clicked < minDay.value) || clicked > maxDay.value) return

  if (props.selectionMode === 'single') {
    commit(clicked)
    return
  }

  if (!draftStart.value || draftEnd.value) {
    draftStart.value = clicked
    draftEnd.value = null
    return
  }

  const value =
    clicked < draftStart.value
      ? { start: clicked, end: draftStart.value }
      : { start: draftStart.value, end: clicked }
  draftStart.value = value.start
  draftEnd.value = value.end
  commit(value)
}

function onTriggerKeydown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    showCalendar()
  }
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (
    event.key !== 'Escape' ||
    !open.value ||
    !(document.activeElement instanceof HTMLElement) ||
    (!floating.value?.contains(document.activeElement) && document.activeElement !== trigger.value)
  ) {
    return
  }
  event.preventDefault()
  event.stopPropagation()
  hideCalendar(true)
}

useClickOutside(floating, () => hideCalendar(), { include: () => [trigger.value] })

watch(
  model,
  () => {
    if (open.value) hydrateDraft()
  },
  { deep: true },
)
watch(
  () => props.selectionMode,
  () => {
    if (open.value) hydrateDraft()
  },
)
onMounted(() => document.addEventListener('keydown', onDocumentKeydown, true))
onUnmounted(() => document.removeEventListener('keydown', onDocumentKeydown, true))
</script>

<template>
  <div
    class="ui-date-input relative w-full font-sans"
    :class="[`ui-date-input--${size}`, { 'is-disabled': disabled, 'is-invalid': invalid }]"
  >
    <label
      v-if="label"
      :for="triggerId"
      class="ui-date-input__label absolute -top-2 left-xs z-raised rounded-sm bg-[linear-gradient(to_bottom,transparent_40%,var(--ui-color-surface)_50%)] px-2xs text-xs font-medium text-muted"
    >
      {{ label }}
    </label>
    <button
      :id="triggerId"
      ref="trigger"
      type="button"
      class="ui-date-input__trigger flex w-full cursor-pointer items-center gap-xs rounded-sm border bg-surface px-sm text-left text-fg [font-family:inherit] focus-visible:border-ring focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-[-1px] focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
      :class="[
        triggerSizeClasses[size],
        { 'border-danger': invalid, 'border-[var(--ui-color-date-input-border)]': !invalid },
      ]"
      :disabled="disabled"
      :aria-disabled="disabled || undefined"
      :aria-invalid="invalid || undefined"
      :aria-describedby="invalid && errorMessage ? errorId : undefined"
      :aria-label="triggerLabel"
      aria-haspopup="dialog"
      :aria-expanded="open"
      :aria-controls="open ? panelId : undefined"
      @click="toggleCalendar"
      @keydown="onTriggerKeydown"
    >
      <Icon
        class="ui-date-input__icon flex-none text-primary-muted"
        name="calendar-days"
        :size="16"
        aria-hidden="true"
      />
      <span
        v-if="displayValue"
        class="ui-date-input__value min-w-0 flex-1 truncate leading-none font-semibold text-[var(--ui-color-date-input-value)]"
      >
        {{ displayValue }}
      </span>
      <span v-else class="ui-date-input__placeholder min-w-0 flex-1 truncate text-muted">
        {{ placeholder }}
      </span>
      <Icon
        class="ui-date-input__chevron flex-none text-[var(--ui-color-date-input-caret)] transition-transform duration-150"
        :class="{ 'is-open rotate-180': open }"
        name="caret-down"
        :size="12"
        aria-hidden="true"
      />
    </button>
    <p
      v-if="invalid && errorMessage"
      :id="errorId"
      class="ui-date-input__error mt-2xs text-xs text-danger"
    >
      {{ errorMessage }}
    </p>

    <OverlayPortal>
      <FloatingLayer
        v-if="open"
        :id="panelId"
        v-model:element="floating"
        kind="popover"
        class="ui-date-input__floating max-h-[var(--ui-floating-available-height,calc(100vh_-_0.75rem))] w-max max-w-[var(--ui-floating-available-width,calc(100vw_-_0.75rem))] overflow-auto rounded-lg border border-border bg-surface p-md shadow-pop"
        :style="floatingStyles"
        role="dialog"
        :aria-label="calendarLabel"
      >
        <FocusTrap :features="focusFeatures">
          <CalendarView
            :weekday-labels="weekdayLabels"
            :month-labels="monthLabels"
            :min="minDay"
            :max="maxDay"
            :start="draftStart"
            :end="draftEnd"
            height="18.75rem"
            @day-click="selectDay"
          />
        </FocusTrap>
      </FloatingLayer>
    </OverlayPortal>
  </div>
</template>
